<template>
    <cic-popup
        :title="item.profileId ? 'Edit Pacing Profile' : 'New Pacing Profile'"
        :saveable="editing"
        class="edit-pacing-profile"
        @blur="$emit('blur')"
        @input="$emit('input')"
    >
        <div class="item-form">
            <ul>
                <li class="form-label-first">
                    {{ $t('Name') }}
                </li>
                <li>
                    <cic-textbox
                        id="profile_name"
                        :enabled="editing"
                        :value="item.name"
                        :maxlength="64"
                        @input="(val) => updateFieldValue('name', val)"
                    />
                </li>
                <li class="form-label">
                    {{ $t('Type') }}
                </li>
                <li>
                    <cic-select
                        id="profile_type"
                        :enabled="editing"
                        :value="item.type"
                        :options="types"
                        @input="(val) => updateFieldValue('type', val)"
                    />
                </li>
                <template v-if="item.type > 0">
                    <li class="form-label">
                        {{ $t('Queue') }}
                    </li>
                    <li>
                        <cic-select
                            id="profile_queue"
                            :enabled="editing"
                            :value="item.treatmentId ? item.treatmentId : null"
                            :options="
                                queues
                                    .filter(
                                        (queue) =>
                                            queue.predictive === (item.type === 5) ||
                                            queue.queueId === item.treatmentId,
                                    )
                                    .map((queue) => ({ value: queue.queueId, label: queue.name }))
                            "
                            @input="(val) => updateFieldValue('treatmentId', val)"
                        />
                    </li>
                </template>
                <template v-if="![4, 5].includes(item.type)">
                    <li class="form-label">
                        {{ $t('Simultaneous Call Cap') }}
                    </li>
                    <li class="inline-form-item">
                        <span class="form-label">{{ $t('Unlimited') }}</span>
                        <cic-checkbox
                            id="profile_unlimited"
                            :enabled="editing"
                            :value="item.maxSimultaneous === -1"
                            @input="(val) => updateFieldValue('maxSimultaneous', val ? -1 : 0)"
                        />
                    </li>
                    <li v-if="item.maxSimultaneous !== -1" class="inline-form-item">
                        <span class="form-label">{{ $t('Limit') }}</span>
                        <cic-textbox
                            id="profile_limit"
                            :enabled="editing"
                            :value="item.maxSimultaneous"
                            :int-only="true"
                            @input="(val) => updateFieldValue('maxSimultaneous', val)"
                        />
                    </li>
                </template>
                <template v-if="[1, 3].includes(item.type)">
                    <li class="form-label">
                        {{ $t(item.type === 1 ? 'Pacing' : 'Initial Pacing') }}
                    </li>
                    <li class="inline-form-item">
                        <cic-textbox
                            id="profile_initial"
                            :enabled="editing"
                            :value="item.initialPacing"
                            :int-only="true"
                            @input="(val) => updateFieldValue('initialPacing', val)"
                        />
                        <span>{{ $t('per agent') }}</span>
                    </li>
                </template>
                <template v-if="[3, 5].includes(item.type)">
                    <li class="form-label">
                        {{ $t('Drop Ratio Goal') }}
                    </li>
                    <li class="inline-form-item">
                        <cic-textbox
                            id="profile_ratio"
                            :enabled="editing"
                            :value="item.dropRatioGoal"
                            :int-only="true"
                            @input="(val) => updateFieldValue('dropRatioGoal', val)"
                        />
                        <span>%</span>
                    </li>
                </template>
                <template v-if="item.type === 3">
                    <li class="form-label">
                        {{ $t('Max. Pacing') }}
                    </li>
                    <li class="inline-form-item">
                        <cic-textbox
                            id="profile_max"
                            :enabled="editing"
                            :value="item.maxPacing"
                            :int-only="true"
                            @input="(val) => updateFieldValue('maxPacing', val)"
                        />
                        <span>{{ $t('per agent') }}</span>
                    </li>
                    <li class="form-label">
                        {{ $t('Min. Pacing') }}
                    </li>
                    <li class="inline-form-item">
                        <cic-textbox
                            id="profile_min"
                            :enabled="editing"
                            :value="item.minPacing"
                            :int-only="true"
                            @input="(val) => updateFieldValue('minPacing', val)"
                        />
                        <span>{{ $t('per agent') }}</span>
                    </li>
                </template>
            </ul>
            <edit-preview
                v-if="item.type === 4 && previewTimeout"
                :editing="editing"
                :item="item"
                @input="updateFieldValue"
            />
            <edit-predictive v-if="item.type === 5" :editing="editing" :item="item" @input="updateFieldValue" />
        </div>
    </cic-popup>
</template>

<script>
    import CicPopup from '@/components/CicPopup';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import CicSelect from '@/components/inputs/CicSelect';
    import CicTextbox from '@/components/inputs/CicTextbox';
    import EditPreview from './EditPreview';
    import EditPredictive from './EditPredictive';

    export default {
        name: 'EditPacingProfile',
        components: {
            CicCheckbox,
            CicPopup,
            CicSelect,
            CicTextbox,
            EditPredictive,
            EditPreview,
        },
        props: {
            item: {
                type: Object,
                required: true,
            },
            predictive: {
                type: Boolean,
                default: false,
            },
            previewTimeout: {
                type: Boolean,
                default: false,
            },
            queues: {
                type: Array,
                required: true,
            },
        },
        emits: ['blur', 'input', 'updateFieldValue'],
        data() {
            return {
                editing: false,
                types: [],
            };
        },
        created() {
            this.editing = this.$store.hasRight('bEditPacingAlgorithms');
            this.types = [{ value: 0, label: this.$t('PACING_TYPE_0') }].concat(
                [1, 3, 4, 5]
                    .filter(
                        (type) =>
                            this.item.type === type ||
                            (this.$store.hasRight('REDCONTACT_INTEGRATION') &&
                                (type !== 4 || this.$store.hasRight('DIAL_RIGHTS_ALLOW_PREVIEW_DIAL')) &&
                                (type !== 5 || this.predictive)),
                    )
                    .map((type) => ({ value: type, label: this.$t(`PACING_TYPE_${type}`) })),
            );
        },
        methods: {
            updateFieldValue(field, val) {
                this.$emit('updateFieldValue', field, val);
            },
        },
    };
</script>
